import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import PageBanner from 'LuxKit/Banners/PageBanner'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import GeoContext from 'contexts/geoContext'
import { END_SPEND_EARN_PROMO_TAG_DATE, PROMO_SPEND_EARN_REGION } from 'constants/tours'
import ModalContext from 'contexts/ModalContext'
import TourSpendEarnDetailModal from 'components/Tours/TourSpendEarnDetailModal'
import { useScreenSizeBetween } from 'hooks/useScreenSize'
import TextLink from 'LuxKit/TextLink'

function TourSpendEarnPageBanner() {
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const showModal = useContext(ModalContext)
  const intl = useIntl()
  const creditEarnListRegion = PROMO_SPEND_EARN_REGION[currentRegionCode]
  const isBetweenTabletToDesktopSize = useScreenSizeBetween('tablet', 'extraLargeDesktop')

  const bestCreditEarn = useMemo<number>(() => {
    const creditEarnList = creditEarnListRegion ?? []
    if (creditEarnList.length) {
      return Math.max(...creditEarnList.map(creditEarn => creditEarn.earn))
    }
    return 0
  }, [creditEarnListRegion])

  const showSpendEarnDetail = useCallback(() => {
    showModal(<TourSpendEarnDetailModal spendEarnList={creditEarnListRegion} maxCredit={bestCreditEarn} />)
  }, [bestCreditEarn, creditEarnListRegion, showModal])

  if (new Date() > END_SPEND_EARN_PROMO_TAG_DATE) {
    return null
  }

  const learnMoreText = intl.formatMessage(
    {
      id: 'uVEJxSf77ouDsQWUhSmA',
      defaultMessage: 'Learn more',
      description: 'Link text to open the spend and earn promotion detail modal',
    },
  )

  const tabletDesktopBanner = <FormattedMessage
    id="7HpkPNGhqbq68peh3Z5c"
    defaultMessage="<b>Spend More, Earn More!</b> Enjoy up to {amount} Luxury Escapes credit for your next booking. Use code: <b>TOURDEAL</b><link>{learnMoreText}</link>"
    description="Promotional banner for spend and earn offer with credit amount and promo code - tablet/desktop version with extra booking text"
    values={{
      amount: <FormatCurrency value={bestCreditEarn} currency={currentCurrency} />,
      learnMoreText,
      b: (chunks) => <b>{chunks}</b>,
      link: (chunks) => <TextLink onClick={showSpendEarnDetail}>{chunks}</TextLink>,
    }}
  />

  const mobileBanner = <FormattedMessage
    id="tyDGob8AD-M2fXWupfsd"
    defaultMessage="<b>Spend More, Earn More!</b> Enjoy up to {amount} Luxury Escapes credit. Use code: <b>TOURDEAL</b><link>{learnMoreText}</link>"
    description="Promotional banner for spend and earn offer with credit amount and promo code - mobile version"
    values={{
      amount: <FormatCurrency value={bestCreditEarn} currency={currentCurrency} />,
      learnMoreText,
      b: (chunks) => <b>{chunks}</b>,
      link: (chunks) => <TextLink onClick={showSpendEarnDetail}>{chunks}</TextLink>,
    }}
  />

  return <PageBanner
    kind="branded"
    align="start"
    tabletAlign="center"
  >
    {isBetweenTabletToDesktopSize ? tabletDesktopBanner : mobileBanner}
  </PageBanner>
}

export default TourSpendEarnPageBanner
